import { useState } from 'react';
import { FileUpload } from './FileUpload';

export const PREDEFINED_MERCHANTS = [
  'black-bear', 'cookies-quartet', 'cova', 'grand-cuisine', 'kubrick', 'luna-cake', 'mannings',
  'martens', 'menwah', 'moon-xian-nong', 'my-little-korner', 'oriental-traffic', 'palate', 'panash',
  'peekapoke', 'popmart', 'pram', 'qpets', 'rains', 'sanrio', 'sensory',
  'shiawase-yakitori', 'siuchung', 'starbuck-taikooshing', 'tanuki', 'tebo', 'ts-brand',
];

export type ReferenceMode = 'predefined' | 'upload';
export type MerchantInputType = 'predefined' | 'custom';

interface MerchantSampleSelectorProps {
  mode: ReferenceMode;
  onModeChange: (mode: ReferenceMode) => void;
  merchantName: string;
  onMerchantNameChange: (name: string) => void;
  referenceImages: File[];
  onReferenceImageAdd: (file: File) => void;
  onReferenceImageRemove: (index: number) => void;
  onOpenReferenceImage: (file: File) => void;
  onPreviewSamples: () => void;
  previewSampleUrls: string[];
  isLoadingPreview: boolean;
  disabled?: boolean;
}

export function MerchantSampleSelector({
  mode,
  onModeChange,
  merchantName,
  onMerchantNameChange,
  referenceImages,
  onReferenceImageAdd,
  onReferenceImageRemove,
  onOpenReferenceImage,
  onPreviewSamples,
  previewSampleUrls,
  isLoadingPreview,
  disabled,
}: MerchantSampleSelectorProps) {
  
  // Determine initial merchant input type based on merchant name
  const getInitialMerchantInputType = (): MerchantInputType => {
    if (!merchantName) return 'custom'; // No merchant name -> custom mode
    return PREDEFINED_MERCHANTS.includes(merchantName) ? 'predefined' : 'custom';
  };
  
  const [merchantInputType, setMerchantInputType] = useState<MerchantInputType>(
    getInitialMerchantInputType()
  );

  return (
    <div className="merchant-sample-selector">
      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`mode-button ${mode === 'predefined' ? 'active' : ''}`}
          onClick={() => onModeChange('predefined')}
          disabled={disabled}
        >
          📋 Specify Merchant
        </button>
        <button
          type="button"
          className={`mode-button ${mode === 'upload' ? 'active' : ''}`}
          onClick={() => onModeChange('upload')}
          disabled={disabled}
        >
          📤 Upload Own Samples
        </button>
      </div>

      {/* Predefined Merchant Mode */}
      {mode === 'predefined' && (
        <div className="predefined-mode">
          {/* Merchant Input Type Selection */}
          <div className="merchant-input-type-selection">
            <label className="radio-label">
              <input
                type="radio"
                name="merchant-input-type"
                value="predefined"
                checked={merchantInputType === 'predefined'}
                onChange={() => setMerchantInputType('predefined')}
                disabled={disabled}
              />
              <span>Select from predefined merchants</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="merchant-input-type"
                value="custom"
                checked={merchantInputType === 'custom'}
                onChange={() => setMerchantInputType('custom')}
                disabled={disabled}
              />
              <span>Enter custom merchant name</span>
            </label>
          </div>

          {/* Predefined Merchant Dropdown */}
          {merchantInputType === 'predefined' && (
            <div className="form-group">
              <label htmlFor="merchant-select">Select Merchant *</label>
              <select
                id="merchant-select"
                value={merchantName}
                onChange={(e) => onMerchantNameChange(e.target.value)}
                disabled={disabled}
                required
              >
                <option value="">-- Select a merchant --</option>
                {PREDEFINED_MERCHANTS.map((merchant) => (
                  <option key={merchant} value={merchant}>
                    {merchant}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Merchant Input */}
          {merchantInputType === 'custom' && (
            <div className="form-group">
              <label htmlFor="merchant-input">Merchant Name *</label>
              <input
                id="merchant-input"
                type="text"
                value={merchantName}
                onChange={(e) => onMerchantNameChange(e.target.value)}
                placeholder="Enter merchant name"
                disabled={disabled}
                required
              />
              <div className="form-help-text">
                <small>Enter a custom merchant name (e.g., newly added merchants)</small>
              </div>
            </div>
          )}

          {merchantName && (
            <div className="preview-section">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onPreviewSamples}
                disabled={disabled || isLoadingPreview}
              >
                {isLoadingPreview ? '⏳ Loading...' : '🔍 Preview Reference Samples'}
              </button>

              {previewSampleUrls.length > 0 && (
                <div className="preview-samples">
                  <h4>Reference Samples ({previewSampleUrls.length})</h4>
                  <div className="preview-grid">
                    {previewSampleUrls.map((url, index) => (
                      <div
                        key={index}
                        className="preview-item"
                        onClick={() => window.open(url, '_blank')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            window.open(url, '_blank');
                          }
                        }}
                        title="Click to open in new tab"
                      >
                        <img src={url} alt={`Sample ${index + 1}`} loading="lazy" />
                        <div className="preview-overlay">
                          <span>🔍 View</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload Own Samples Mode */}
      {mode === 'upload' && (
        <div className="upload-mode">
          <div className="form-group">
            <label htmlFor="merchant-name-input">Merchant Name (Optional)</label>
            <input
              id="merchant-name-input"
              type="text"
              value={merchantName}
              onChange={(e) => onMerchantNameChange(e.target.value)}
              placeholder="e.g., My Custom Merchant"
              disabled={disabled}
            />
            <div className="form-help-text">
              <small>Optional: Provide a name for reference purposes</small>
            </div>
          </div>

          <div className="subsection">
            <h3>Upload Reference Images *</h3>
            <FileUpload
              onFileSelect={onReferenceImageAdd}
              disabled={disabled}
              multiple={true}
            />

            {referenceImages.length > 0 && (
              <div className="reference-images-list">
                <h4>Uploaded References ({referenceImages.length})</h4>
                <div className="reference-items">
                  {referenceImages.map((file, index) => (
                    <div key={index} className="reference-item">
                      <div className="reference-item-content">
                        <div className="reference-icon">🖼️</div>
                        <div className="reference-info">
                          <div className="reference-name" title={file.name}>
                            {file.name}
                          </div>
                          <div className="reference-meta">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                      </div>
                      <div className="reference-item-actions">
                        <button
                          type="button"
                          className="btn btn-icon btn-icon-secondary"
                          onClick={() => onOpenReferenceImage(file)}
                          title="Open image in new tab"
                          disabled={disabled}
                        >
                          🔍
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon"
                          onClick={() => onReferenceImageRemove(index)}
                          title="Remove reference image"
                          disabled={disabled}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {referenceImages.length === 0 && (
              <div className="form-help-text">
                <small>⚠️ At least one reference image is required in upload mode</small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
